//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

/**
 * The chipp.ai assistant, embedded in an iframe we own.
 *
 * Identity travels in the iframe's OWN url as `?chipp_external_id=<handle>`;
 * chipp reads it from the query string and Custom Actions receive it as
 * {{system.query.chipp_external_id}}, which verify-chipp resolves back to this
 * user. Note that must be `{{system.query.chipp_external_id}}` in chipp Studio
 * — `{{system.external_id}}` is only populated for top-level navigation and
 * resolves empty here.
 *
 * The chat session is bound to the user's chipp consumer by posting
 * `chipp:identify { email, name, token }` to the frame once it sends
 * `chipp:ready` — the same message chipp's widget sends from setUser (fixed on
 * chipp's side in CHIPPD-1771). The app only speaks that handshake in widget
 * mode, hence `#/chat?widget=true`; the bare root boots as a standalone PWA and
 * ignores it. Widget mode has no past-conversations list — accepted.
 *
 * We deliberately do NOT use chipp's widget itself: it builds its own iframe
 * url, so the handle above can't ride along, and the data plane falls back to
 * the model-copied channelUserId. Tested 2026-09-16 against both.
 *
 * Binding gives each user their own conversation within a browser. The frame
 * alone does not carry history to another browser — widget mode shows no list
 * — which is what the History menu below is for.
 *
 * History: chipp keeps each consumer's conversations server-side. The list
 * comes from Lasso's /chipp/sessions, which calls chipp's supported Builder API
 * filtered to this user's consumer. A conversation is reopened by building a
 * new frame with `chat_session=<id>`, and a fresh random id starts an empty
 * one — chipp says that parameter is internal and unsupported (2026-09-16), so
 * if it goes, opening a chat or New chat may land on the latest conversation.
 *
 * A live chat app freezes if it is identified as a second user, so identity
 * changes must always get a NEW frame — see the impersonationUserId watcher.
 * Logout needs nothing here: the bubble unmounts with the logged-in user.
 */
import { mapGetters } from 'vuex'

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const randomUuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) { return crypto.randomUUID() }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    return (c === 'x' ? r : (r & 3 | 8)).toString(16)
  })
}

export default {
  name: 'AssistantChat',

  props: {
    // The bubble only loads the chat when opened, so we don't start a chat
    // session on every page view. The page embed passes true immediately.
    active: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      src: null,
      state: 'idle',
      // { email, name, token } for the frame currently loaded; sent on chipp:ready
      identity: null,
      view: 'chat',
      menuOpen: false,
      sessions: [],
      historyState: 'idle',
      nextCursor: null,
      search: ''
    }
  },

  computed: {
    ...mapGetters({
      impersonationUserId: 'user/getImpersonationUserId'
    })
  },

  watch: {
    active: {
      immediate: true,
      handler (isActive) {
        if (isActive && !this.src) { this.load() }
      }
    },

    /**
     * Rebuild the frame when the acting identity changes.
     *
     * The identity handle is baked into the iframe's url at load time and is
     * never re-read, and this component is deliberately kept mounted so that
     * closing and reopening the bubble does not discard the conversation.
     * Together those mean that starting or stopping impersonation with the chat
     * ALREADY OPEN leaves the previous user's handle in the url: the assistant
     * keeps answering as whoever opened it while the rest of the page shows the
     * impersonated user. Silently — nothing on screen says which identity the
     * chat is using.
     *
     * The dangerous direction is a super admin's wider scope leaking into a
     * session that looks like a narrower user's. `impersonate -> then open`
     * has always been correct; only `open -> then impersonate` was broken.
     *
     * Clearing `src` unmounts the iframe (v-if), which discards the previous
     * transcript along with it. That is intended, not collateral: a different
     * identity must not inherit the last one's conversation.
     */
    impersonationUserId (next, previous) {
      if (next === previous) { return }
      if (!this.src) { return }
      this.src = null
      this.identity = null
      this.state = 'idle'
      // The previous identity's history must not stay on screen either.
      this.view = 'chat'
      this.menuOpen = false
      this.sessions = []
      this.historyState = 'idle'
      if (this.active) { this.load() }
    }
  },

  mounted () {
    window.addEventListener('message', this.onMessage)
  },

  beforeDestroy () {
    window.removeEventListener('message', this.onMessage)
  },

  methods: {
    async load (sessionId) {
      const slug = this.$config.chippAppId
      if (!slug) { this.state = 'error'; return }
      // Always a fresh frame, including on "Try again": a chat app that has
      // already been identified must never be identified again.
      this.src = null
      this.identity = null
      this.state = 'loading'
      // Only the latest load may set up the frame. Starting impersonation can
      // overlap two inits; if the older one answered last, the chat would be
      // identified as the admin while the page shows the impersonated user.
      // Not reactive on purpose, and set here rather than in created(): the
      // immediate `active` watcher calls load() before created() runs.
      const seq = this.loadSeq = (this.loadSeq || 0) + 1
      try {
        const { data } = await this.$axios.post('/chipp/init')
        if (seq !== this.loadSeq) { return }
        if (!data || !data.chippExternalId) { this.state = 'error'; return }
        this.identity = { email: data.email, name: data.name, token: data.chippToken }
        // Identity rides in the iframe url, never in Lasso's address bar.
        const session = UUID.test(sessionId) ? `&chat_session=${sessionId}` : ''
        this.src = `https://${slug}.on.chipp.ai/?chipp_external_id=${encodeURIComponent(data.chippExternalId)}#/chat?widget=true${session}`
        // state flips to 'ready' on the iframe's load event.
      } catch (e) {
        if (seq === this.loadSeq) { this.state = 'error' }
      }
    },

    openSession (id) {
      this.view = 'chat'
      this.load(id)
    },

    newChat () {
      this.view = 'chat'
      this.load(randomUuid())
    },

    showHistory () {
      this.view = 'history'
      this.fetchHistory()
    },

    async fetchHistory (cursor) {
      if (!this.identity) { return }
      // Marks which identity this request belongs to; a reply for an earlier one is dropped.
      const marker = this.identity
      const params = {}
      const search = this.search.trim().slice(0, 200)
      if (search) { params.search = search }
      if (cursor) { params.cursor = cursor }
      this.historyState = 'loading'
      if (!cursor) { this.sessions = [] }
      try {
        const { data } = await this.$axios.get('/chipp/sessions', { params })
        if (this.identity !== marker) { return }
        const sessions = (data && data.sessions) || []
        this.sessions = cursor ? this.sessions.concat(sessions) : sessions
        this.nextCursor = (data && data.nextCursor) || null
        this.historyState = 'ready'
      } catch (e) {
        if (this.identity === marker) { this.historyState = 'error' }
      }
    },

    formatWhen (value) {
      const date = new Date(value)
      return isNaN(date) ? '' : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    },

    frameWindow () {
      return this.$refs.frame ? this.$refs.frame.contentWindow : null
    },

    onMessage (event) {
      const origin = `https://${this.$config.chippAppId}.on.chipp.ai`
      const frame = this.frameWindow()
      if (event.origin !== origin || !frame || event.source !== frame) { return }
      const type = event.data && event.data.type
      if (type === 'chipp:ready' && this.identity && this.identity.token) {
        frame.postMessage({ type: 'chipp:identify', payload: this.identity }, origin)
      } else if (type === 'chipp:identify-ack') {
        const payload = event.data.payload || {}
        if (payload.success !== true) {
          // chipp freezes a chat whose identify was rejected.
          console.warn('[assistant] chipp rejected identify:', payload.error)
          this.state = 'error'
        }
      }
    }
  }
}
